import { Box, Divider, Flex, HStack, Text } from "@chakra-ui/react";
import React from "react";
import { HiOutlineTrash } from "react-icons/hi";

import { IconButton } from "../../../components";
import DragHandleDots from "../../../components/Icons/DragHandleDots";
import { GuideQuestionAnalysis } from "../../graphql";
import {
  QuestionSuggestionPreviewState,
  SuggestionFieldTarget,
} from "./GuideAssistantV2/types";
import { QuestionAnalysisPanel } from "./QuestionAnalysisPanel";
import QuestionForm from "./QuestionForm";

type AiInterviewerGuideQuestionItemProps = {
  index: number;
  questionId: string;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
  isDragging: boolean;
  onDelete: () => void;
  readOnly?: boolean;
  canEnableQuestionWeights?: boolean;
  questionAnalysis?: GuideQuestionAnalysis | null;
  onSuggestionsAccepted?: () => void;
  reservePanelSpace?: boolean;
  highlighted?: boolean;
  assistantPreview?:
    | (QuestionSuggestionPreviewState & { questionIndex: number })
    | null;
  applyingSuggestionId?: string | null;
  ignoringSuggestionId?: string | null;
  onResolveAssistantPreviewField?: (
    questionId: string,
    fallbackQuestionIndex: number,
    target: SuggestionFieldTarget,
    decision: "accept" | "reject"
  ) => void;
  onResolveAssistantPreviewAll?: (
    questionId: string,
    fallbackQuestionIndex: number
  ) => void;
  onIgnoreAssistantPreview?: (
    questionId: string,
    fallbackQuestionIndex: number
  ) => void;
};

const AiInterviewerGuideQuestionItem: React.FC<
  AiInterviewerGuideQuestionItemProps
> = ({
  index,
  questionId,
  dragHandleProps,
  isDragging,
  onDelete,
  readOnly,
  canEnableQuestionWeights,
  questionAnalysis,
  onSuggestionsAccepted,
  reservePanelSpace,
  highlighted,
  assistantPreview,
  applyingSuggestionId,
  ignoringSuggestionId,
  onResolveAssistantPreviewField,
  onResolveAssistantPreviewAll,
  onIgnoreAssistantPreview,
}) => {
  const hasAnalysis = questionAnalysis != null;
  const useSplitLayout = hasAnalysis || reservePanelSpace;

  return (
    <HStack
      id={`guide-question-card-${questionId}`}
      data-guide-question-index={index}
      bg={isDragging ? "gray.100" : "gray.50"}
      position="relative"
      overflow="hidden"
      borderRadius="lg"
      border="1px solid"
      borderColor="gray.200"
      py={3}
      pl={3}
      pr={3}
      alignItems="stretch"
      w="100%"
      minW={0}
      boxShadow={isDragging || !!assistantPreview ? "md" : undefined}
      spacing={0}
      scrollMarginTop="90px"
    >
      <Flex direction="column" minW="6" flexShrink={0}>
        <Flex align="center" justify="center" h="32px" {...dragHandleProps}>
          <DragHandleDots width={5} height={5} />
        </Flex>
      </Flex>
      <Box flex="1" minW={0} w={useSplitLayout ? undefined : "100%"}>
        <Flex align="center" justify="space-between" mb={2}>
          <Text
            fontSize="xs"
            fontWeight="semibold"
            color="gray.500"
            textTransform="uppercase"
            lineHeight="16px"
          >
            Question {index + 1}
          </Text>
          {!readOnly && (
            <IconButton
              aria-label="Delete question"
              icon={<HiOutlineTrash size={20} />}
              size="sm"
              variant="ghost"
              color="gray.400"
              _hover={{ color: "red.500" }}
              onClick={onDelete}
              isDisabled={!!assistantPreview}
            />
          )}
        </Flex>
        <QuestionForm
          index={index}
          readOnly={readOnly}
          canEnableQuestionWeights={canEnableQuestionWeights}
          assistantPreview={assistantPreview}
          assistantPreviewApplying={
            assistantPreview?.suggestionId === applyingSuggestionId
          }
          assistantPreviewIgnoring={
            assistantPreview?.suggestionId === ignoringSuggestionId
          }
          onResolveAssistantPreviewField={(target, decision) =>
            onResolveAssistantPreviewField?.(
              questionId,
              index,
              target,
              decision
            )
          }
          onResolveAssistantPreviewAll={() =>
            onResolveAssistantPreviewAll?.(questionId, index)
          }
          onIgnoreAssistantPreview={() =>
            onIgnoreAssistantPreview?.(questionId, index)
          }
        />
      </Box>
      {hasAnalysis ? (
        <>
          <Divider orientation="vertical" mx={4} />
          <Box
            flex="1"
            minW="360px"
            maxW="480px"
            maxH="600px"
            overflowY="auto"
            px={4}
          >
            <QuestionAnalysisPanel
              questionIndex={index}
              analysis={questionAnalysis}
              onAccepted={onSuggestionsAccepted}
            />
          </Box>
        </>
      ) : reservePanelSpace ? (
        <Box flex="1" minW="360px" maxW="480px" />
      ) : null}
    </HStack>
  );
};

export default AiInterviewerGuideQuestionItem;
